import { Skeleton, Typography, Button, Modal } from "@mui/material";
import { Paper } from "@mui/material";
import { useTheme } from "@mui/material";

import { Box } from "@mui/material";

import Iconify from "src/components/iconify";

import { useDispatch, useSelector } from "react-redux";
import { setAdvancedView, setExports } from "src/context/reducers/filter-settings/filterSlice";
import { Tabs, Tab, ButtonGroup } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RootState } from "src/context/store";
import { GridFilterModel } from "@mui/x-data-grid";
import { GridSortModel } from "@mui/x-data-grid";
import { makeTrafficQuery, useDataQuery, useKpisQuery, useExportMutation, useExportStatusQuery } from "src/context/api/data/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useLocales } from "src/locales";
import { Icon } from "@iconify/react";
import GenericDataGrid from "src/components/data-grid/generic-data-grid";

import "src/components/data-grid/data-grid.scss";
import { useBoolean } from "src/hooks/use-boolean";
import { merge } from "lodash";
import { enqueueSnackbar } from "notistack";

const tabs = {
    activations: [
        {
            label: 'dashboard.global.keywords',
            value: 0,
            groupby: ["account", "campaign_name", "ad_group_name", "keyword", "match_type"],
        },
        {
            label: 'dashboard.global.countries',
            value: 1,
            groupby: ["region_name"],
        },
        {
            label: 'dashboard.global.devices',
            value: 2,
            groupby: ["device_type"],
        },
        {
            label: 'dashboard.global.date',
            value: 3,
            groupby: ["date"],
        },
        {
            label: 'dashboard.global.campaigns',
            value: 4,
            groupby: ["account", "campaign_name"],
        },
        {
            label: 'dashboard.global.ad_groups',
            value: 5,
            groupby: ["account", "campaign_name", "ad_group_name"],
        },
        {
            label: 'dashboard.global.search_terms',
            value: 6,
            groupby: ["account", "campaign_name", "ad_group_name", "keyword", "match_type", "search_term"],
        },
        {
            label: 'dashboard.global.campaign_type',
            value: 7,
            groupby: ["campaign_type"],
        },
    ],
    base: [
        {
            label: 'dashboard.global.keywords',
            value: 0,
            groupby: ["search_term"],
        },
        {
            label: 'dashboard.global.countries',
            value: 1,
            groupby: ["country"],
        },
        {
            label: 'dashboard.global.devices',
            value: 2,
            groupby: ["device"],
        },
        {
            label: 'dashboard.global.date',
            value: 3,
            groupby: ["date"],
        },
    ]
};



export default function TabulableTable() {
    const { t } = useLocales();
    const theme = useTheme();
    const dispatch = useDispatch();
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: RootState) => state.filters);
    const loadedPlatforms = useSelector((state: RootState) => state.filters.loadedPlatform);
    const mode = useSelector((state: RootState) => state.filters.extraOptions.mode);
    const [value, setValue] = useState(0);
    const isAdvancedView = useSelector((state: RootState) => state.filters.isAdvancedView);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: mode === "activations" ? "ad_cost" : "brand_searches", sort: "desc" }]);
    const [filters, setFilterModel] = useState<GridFilterModel>({
        items: [],
    });
    const [exportMutation] = useExportMutation();

    useEffect(() => {
        if (loadedPlatforms.length > 1 && !tabs[mode as keyof typeof tabs].some((tab) => tab.value === 8)) {
            tabs[mode as keyof typeof tabs].push({
                label: 'dashboard.filters.platforms',
                value: 8,
                groupby: ["platform"],
            });
        }
        else {
            tabs[mode as keyof typeof tabs] = tabs[mode as keyof typeof tabs].filter((tab) => tab.value !== 8);
        }
    }, [loadedPlatforms]);

    useEffect(() => {
        if (mode === "activations") {
            setSortModel([{ field: "ad_cost", sort: "desc" }]);
            setFilterModel({
                items: [],
            });
        } else {
            setSortModel([{ field: "brand_searches", sort: "desc" }]);
            setFilterModel({
                items: [],
            });
        }
    }, [mode]);

    const handleChange = (event: any, newValue: any) => {
        setValue(newValue);

        // Reset du sort model lors du changement de tab pour éviter les problèmes de validation
        if (newValue === 3) {
            setSortModel([{ field: filterStore?.timestep.toLowerCase().replace("date", mode === "activations" ? "date_day" : "date"), sort: "desc" }]);
        } else {
            const defaultField = mode === "activations" ? "ad_cost" : "brand_searches";
            setSortModel([{ field: defaultField, sort: "desc" }]);
        }

        // Reset aussi les filtres si nécessaire
        setFilterModel({
            items: [],
        });
    };

    const { data: kpis, isLoading: isFetchingKPIs, error: kpisError } = useKpisQuery();
    const order_by = useMemo(() => {
        const validSortModel = sortModel.filter((item) => {
            const kpi = kpis?.find((k) => k.id === item.field);
            if (kpi) {
                const kpiVizMatch = ((mode === "activations" && kpi?.viz === "activation") || (mode !== "activations" && kpi?.viz === "base"));
                return kpi.viz === "both" || kpiVizMatch || ["date", "date_day", "year_week", "year_month"].includes(item.field);
            }
            return false;
        });
        return validSortModel.length > 0
            ? validSortModel.map((item) => [item.field, item.sort] as [string, string])
            : [];
    }, [sortModel, mode, kpis]);

    const groupby = useMemo(() => tabs[mode as keyof typeof tabs].find((tab) => tab.value === value)?.groupby ?? [], [tabs, value, mode]);


    const queryOpts = {
        group_by: groupby,
        start: filterStore?.start,
        end: filterStore?.end,
        interval: filterStore?.timestep.toLowerCase(),
        page_size: paginationModel.pageSize,
        page: paginationModel.page + 1,
        search_terms: filterStore?.keywords ?? [],
        countries: filterStore?.countries ?? [],
        devices: filterStore?.devices ?? [],
        platforms: filterStore?.platforms?.map((platform: any) => platform.toUpperCase()) ?? [],
        start_compare: filterStore?.compare ? filterStore?.compareStart : undefined,
        end_compare: filterStore?.compare ? filterStore?.compareEnd : undefined,
        mode,
        output: "table",
        filters,
        workspace_id: workspace?.id as number,
        extra_options: filterStore?.extraOptions,
        order_by
    } as const;

    const { data: { data: tableData, total_rows = 0, total_pages = 0, current_page = 0, page_size = 0, query_job_id: queryID } = {}, isLoading: isLoadingTableData, isFetching: isFetchingTableData, error: tableDataError } = useDataQuery(queryOpts, {
        skip: !filterStore?.timestep || !filterStore?.compareStart || !filterStore?.compareEnd || !kpis?.length || groupby.length === 0,
    });

    return (
        <Paper
            sx={{
                backgroundColor: theme.palette.mode === "dark" ? '#212b36' : 'white',
                boxShadow: '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
                minHeight: 700,
                borderRadius: 2,
                marginTop: 3,
                marginBottom: 1
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 4, pt: 2, mb: 1 }}>
                <Tabs value={value} onChange={handleChange} aria-label={t('dashboard.brandTraffic.basic_tabs_example')}>
                    {tabs[mode as keyof typeof tabs].map((tab) => (
                        <Tab key={tab.value} value={tab.value} label={t(tab.label)} />
                    ))}
                </Tabs>
                <ButtonGroup
                    size="small"
                    aria-label="contained primary button group"
                    sx={{
                        border: '1px solid rgba(226, 229, 233, 0.16)',
                        borderRadius: 1,
                        padding: '3px',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            size: '12px',
                            pl: 1,
                            pr: 1,
                            color: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.grey[300]
                        }}
                    >
                        {t('dashboard.global.advanced')}
                    </Typography>
                    {[
                        {
                            label: (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Icon icon="material-symbols:grid-off-rounded" style={{ fontSize: '14px', color: theme.palette.mode === 'light' ? 'rgb(64, 75, 86)' : undefined }} />
                                </Box>
                            ), isActive: !isAdvancedView
                        },
                        {
                            label: (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Icon icon="bi:grid-3x3-gap-fill" style={{ fontSize: '14px', color: theme.palette.mode === 'light' ? 'rgb(64, 75, 86)' : undefined }} />
                                </Box>
                            ), isActive: isAdvancedView
                        }
                    ].map(({ label, isActive }, index) => (
                        <Button
                            key={typeof label === 'string' ? label : 'Advanced'}
                            variant={isActive ? 'contained' : 'outlined'}
                            onClick={() => dispatch(setAdvancedView(index === 1))}
                            sx={{
                                ...(isActive && {
                                    border: 0,
                                    backgroundColor: (theme) => `${theme.palette.primary.main}30`,
                                    color: (theme) => theme.palette.primary.light,
                                    borderRadius: 3,
                                    borderTopRightRadius: index === 0 ? 0 : 3,
                                    borderBottomRightRadius: index === 0 ? 0 : 3,
                                    borderTopLeftRadius: index === 1 ? 0 : 3,
                                    borderBottomLeftRadius: index === 1 ? 0 : 3,
                                    '&:hover': {
                                        backgroundColor: (theme) => `${theme.palette.primary.main}30`,
                                        border: 0,
                                        boxShadow: 'none'
                                    }
                                }),
                                ...(!isActive && {
                                    border: 0,
                                    borderRadius: 3,
                                    borderTopRightRadius: index === 0 ? 0 : 3,
                                    borderBottomRightRadius: index === 0 ? 0 : 3,
                                    borderTopLeftRadius: index === 1 ? 0 : 3,
                                    borderBottomLeftRadius: index === 1 ? 0 : 3,
                                    color: (theme) => theme.palette.grey[300],
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        border: 0,
                                        boxShadow: 'none',
                                    }
                                }),
                            }}
                        >
                            {label}
                        </Button>
                    ))}
                </ButtonGroup>
            </Box>

            <Box >
                {isFetchingTableData || (!tableData && !tableDataError)
                    ? <Skeleton width={'100%'} height={680} style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }} variant="rectangular" animation={"wave"}>
                        <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                        <Typography color={"success.main"}>Active</Typography>
                    </Skeleton>
                    : tableData &&
                    <Box height={"auto"}>
                        <GenericDataGrid
                            pinnedColumns={groupby ?? []}
                            data={tableData as any}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            paginationMode="server"
                            rowCount={total_rows}
                            sortModel={sortModel}
                            onSortModelChange={setSortModel}
                            filterModel={filters}
                            onFilterModelChange={setFilterModel}
                            onExport={() => {
                                exportMutation({queryId: queryID as string, name: `[${workspace?.name}] Traffic details export`}).unwrap().then((res) => {
                                    enqueueSnackbar("Export is running in background, you will be notified when it's done", { variant: "success" });
                                })
                                .catch((err) => {
                                    enqueueSnackbar("Error exporting data", { variant: "error" });
                                });
                            }}
                        />
                    </Box>
                }
            </Box>
        </Paper>
    )
}

const ExportModal = ({ open, onClose, onStartExport }: { open: boolean, onClose: () => void, onStartExport: (exportType: "full" | "page") => void }) => {
    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                bgcolor: 'background.paper',
                border: '2px solid #000',
            }}>
                <Typography>Would you like to export the full data or just the current page?</Typography>
                <Button onClick={() => onStartExport("full")}>Full</Button>
                <Button onClick={() => onStartExport("page")}>Page</Button>
            </Box>
        </Modal>
    )
}